import { useEffect, useRef } from "react";
import type { Toast } from "../../core/store";
import { useLabels, useMapCore, useMapStore } from "../hooks";
import { Icon } from "../icons";
import type { IconName } from "../icons";
import { Button } from "../primitives/Button";

export type ToastsProps = { autoDismissMs?: number; actionDismissMs?: number; className?: string };

export const toastDismissMs = 5000;

export const toastActionDismissMs = 8000;

const severityIcons: Record<Toast["severity"], IconName> = { info: "info", success: "success", warning: "warning", danger: "error" };

export function Toasts({ autoDismissMs = toastDismissMs, actionDismissMs = toastActionDismissMs, className }: ToastsProps) {
  const core = useMapCore();
  const labels = useLabels();
  const toasts = useMapStore(a => a.toasts);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  useEffect(() => {
    const live = new Set(toasts.map(a => a.id));

    for (const toast of toasts) {
      if (timers.current.has(toast.id)) continue;

      const delay = toast.action ? actionDismissMs : autoDismissMs;

      timers.current.set(
        toast.id,
        setTimeout(() => {
          timers.current.delete(toast.id);
          core.store.getState().dismissToast(toast.id);
        }, delay)
      );
    }

    for (const [id, handle] of timers.current) {
      if (live.has(id)) continue;

      clearTimeout(handle);
      timers.current.delete(id);
    }
  }, [toasts, core, autoDismissMs, actionDismissMs]);

  useEffect(() => {
    const map = timers.current;

    return () => {
      for (const handle of map.values()) clearTimeout(handle);

      map.clear();
    };
  }, []);

  return (
    <div className={["fxmap-toasts", className ?? ""].filter(Boolean).join(" ")} data-fxmap="toasts" aria-live="polite" aria-relevant="additions">
      {toasts.map(toast => (
        <div key={toast.id} className={["fxmap-toast", "fxmap-card", `fxmap-toast-${toast.severity}`].join(" ")} role={toast.severity === "danger" ? "alert" : "status"} data-severity={toast.severity}>
          <span className="fxmap-toast-stripe" aria-hidden="true" />
          <Icon name={severityIcons[toast.severity]} className="fxmap-toast-icon" />
          <span className="fxmap-toast-text">{toast.text}</span>
          {toast.action ? (
            <Button
              kind="text"
              size="sm"
              label={toast.action.label}
              className="fxmap-toast-action"
              action="toast-action"
              onClick={() => {
                toast.action?.run();
                core.store.getState().dismissToast(toast.id);
              }}
            />
          ) : null}
          <Button kind="icon" size="sm" icon="close" label={labels.dismiss} className="fxmap-toast-close" action="toast-dismiss" onClick={() => core.store.getState().dismissToast(toast.id)} />
        </div>
      ))}
    </div>
  );
}
